const math = require('mathjs');
const _ = require('lodash');
const { binGeometryFromBin } = require('./geometry/bin-geometry');

/**
 * A square matrix of feed heights in meters. Values outside the circular bin are null.
 * @typedef {[Number|null]} HeightMapData
 */

exports.xyToPolar = function ([x, y], resolution) {
  const xc = x - ((resolution - 1) / 2);
  const yc = y - ((resolution - 1) / 2);
  const r = Math.sqrt(xc ** 2 + yc ** 2);
  const theta = Math.atan2(xc, yc);
  return [r, theta];
};

exports.polarToXY = function ([r, theta], resolution) {
  return [r * Math.sin(theta) + ((resolution - 1) / 2), r * Math.cos(theta) + ((resolution - 1) / 2)];
};

exports.resolutionOf = function (heightMap) {
  return heightMap[0].length;
};

exports.countHeightMapValues = function (heightMap) {
  return _.flatten(heightMap).filter(x => x !== null).length;
};

/**
 * Fills in the null values of a height map with the minimum feed height
 * @param {HeightMapData} heightMap
 * @param {Object} binGeometry
 */
exports.completeHeightMapAssumingNoFeed = function (heightMap, binGeometry) {
  const resolution = exports.resolutionOf(heightMap);

  for (let x = 0; x < resolution; x++) {
    for (let y = 0; y < resolution; y++) {
      if (binGeometry.isHeightMapCoordinateInBin(x, y, resolution) && heightMap[x][y] === null) {
        heightMap[x][y] = binGeometry.feedHeightConstraint(x, y, resolution).min;
      }
    }
  }
};

/**
 * Makes a height map of an empty bin.
 * @param {Number} resolution
 * @param {Object} bin
 * @returns {HeightMapData}
 */
exports.heightMapOfEmptyBin = (resolution, bin) => {
  const binGeometry = binGeometryFromBin(bin);
  const heightMap = exports.makeEmptyHeightMap(resolution);
  exports.completeHeightMapAssumingNoFeed(heightMap, binGeometry);
  return heightMap;
};

/**
 * Makes a height map of a bin filled to the top of the topper.
 */
exports.heightMapOfCompletelyFullBin = (resolution, bin) => {
  const binGeometry = binGeometryFromBin(bin);
  const heightMap = exports.makeEmptyHeightMap(resolution);
  for (let x = 0; x < resolution; x++) {
    for (let y = 0; y < resolution; y++) {
      if (binGeometry.isHeightMapCoordinateInBin(x, y, resolution)) {
        heightMap[x][y] = binGeometry.feedHeightConstraint(x, y, resolution).max;
      }
    }
  }
  return heightMap;
};

/**
 * Finds the minimum and maximum feed heights for a given entry of a height map
 * @param x {number} x co-ordinate in height map
 * @param y {number} y co-ordinate in height map
 * @param resolution {number} the resolution of the height map
 * @param bin {Object} bin parameters
 * @returns {{min: number, max: number}}
 */
exports.feedHeightConstraint = function (x, y, resolution, bin) {
  return binGeometryFromBin(bin).feedHeightConstraint(x, y, resolution);
};

exports.downScaleHeightMap = (binGeometry, heightMap, scaleFactor) => {
  const originalResolution = exports.resolutionOf(heightMap);
  if (!Number.isInteger(scaleFactor) || scaleFactor < 1) {
    throw new Error('invalid scale factor');
  }
  if (originalResolution % scaleFactor !== 0) {
    throw new Error('scale factor does not match height map resolution');
  }

  const newResolution = originalResolution / scaleFactor;

  const newHeightMap = exports.makeEmptyHeightMap(newResolution);
  // Loop over every new pixel
  for (let x = 0; x < newResolution; x++) {
    for (let y = 0; y < newResolution; y++) {
      if (!binGeometry.isHeightMapCoordinateInBin(x, y, newResolution)) {
        continue;
      }
      const heightsToAverage = [];
      //  Loop over every original pixel
      for (let i = 0; i < scaleFactor; i++) {
        for (let j = 0; j < scaleFactor; j++) {
          const height = heightMap[x * scaleFactor + i][y * scaleFactor + j];
          if (height !== null) {
            heightsToAverage.push(height);
          }
        }
      }
      if (heightsToAverage.length > 0) {
        newHeightMap[x][y] = (math.mean(heightsToAverage));
      }
    }
  }

  return newHeightMap;
};

exports.makeEmptyHeightMap = (resolution) => {
  const heightMap = [];
  for (let y = 0; y < resolution; y++) {
    const newRow = [];
    for (let x = 0; x < resolution; x++) {
      newRow.push(null);
    }
    heightMap.push(newRow);
  }
  return heightMap;
};

exports.copyHeightMap = (heightMap) => _.cloneDeep(heightMap);

exports.makeHeightMapOfConstantHeight = (binGeometry, resolution, height) => {
  const heightMap = exports.makeEmptyHeightMap(resolution);

  // Change each entry in the height map to the hopper's height
  for (let y = 0; y < resolution; y++) {
    for (let x = 0; x < resolution; x++) {
      if (binGeometry.isHeightMapCoordinateInBin(x, y, resolution)) {
        heightMap[x][y] = height;
      }
    }
  }
  return heightMap;
};

exports.isHeightMapEmpty = (heightMap) => {
  const resolution = exports.resolutionOf(heightMap);
  for (let y = 0; y < resolution; y++) {
    for (let x = 0; x < resolution; x++) {
      if (heightMap[y][x] !== null) {
        return false;
      }
    }
  }
  return true;
};

/**
 * Combine height maps into a single height map, taking the earliest non-null values.
 */
exports.combineHeightMaps = (...heightMaps) => {
  const resolution = exports.resolutionOf(heightMaps[0]);

  // ensure all height maps are the same resolution
  for (let i = 1; i < heightMaps.length; i++) {
    if (exports.resolutionOf(heightMaps[i]) !== resolution) {
      throw new Error('height maps must all be the same resolution');
    }
  }

  const combinedHeightMap = exports.makeEmptyHeightMap(resolution);
  for (let y = 0; y < resolution; y++) {
    for (let x = 0; x < resolution; x++) {
      for (let i = 0; i < heightMaps.length; i++) {
        const height = heightMaps[i][x][y];
        if (height !== null) {
          combinedHeightMap[x][y] = height;
          break;
        }
      }
    }
  }

  return combinedHeightMap;
};

/**
 * @param {HeightMapData} heightMap
 * @param {Object} binGeometry
 * @returns {number} the volume in m^3
 */
exports.volumeFromHeightMap = (heightMap, binGeometry) => {
  const resolution = exports.resolutionOf(heightMap);
  const metresPerHeightMapPixel = binGeometry.metresPerHeightMapPixel(resolution);

  let totalColumnHeight /* m */ = 0;
  const singleColumnBaseArea /* m2 */ = metresPerHeightMapPixel * metresPerHeightMapPixel;

  for (let y = 0; y < resolution; y++) {
    for (let x = 0; x < resolution; x++) {
      if (heightMap[x][y] !== null) {
        const feedHeightUntilBin = heightMap[x][y] - binGeometry.feedHeightConstraint(x, y, resolution).min;
        totalColumnHeight += feedHeightUntilBin;
      }
    }
  }

  return totalColumnHeight * singleColumnBaseArea;
};

exports.binCoordToHeightMapCoord = (coordinate /* m */, resolution, bin) => {
  const binRadius = bin.dimensions.body.radius;
  return (((coordinate + binRadius) / (binRadius * 2)) * resolution);
};

exports.heightMapCoordToBinCoord = (coordinate /* no unit */, resolution, bin) => {
  const binRadius = bin.dimensions.body.radius;
  return (coordinate / resolution) * (binRadius * 2) - binRadius;
};

/**
 * @param {HeightMapData} heightMap
 * @returns {number} the average height of the height map
 */
exports.averageHeight = (heightMap) => {
  const resolution = exports.resolutionOf(heightMap);

  let totalHeight = 0;
  let totalHeightCount = 0;
  for (let y = 0; y < resolution; y++) {
    for (let x = 0; x < resolution; x++) {
      if (heightMap[x][y] !== null) {
        totalHeight += heightMap[x][y];
        totalHeightCount++;
      }
    }
  }

  if (totalHeightCount === 0) {
    throw new Error('height map must not be empty');
  }
  return totalHeight / totalHeightCount;
};

